import { Suspense, useCallback } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '@/lib/auth/auth-context'
import { isDeliberateSignOut } from '@/lib/auth/sign-out-intent'
import { useIdleTimeout, type IdleExpiry } from '@/lib/auth/use-idle-timeout'

import { AppHeader } from './AppHeader'
import { IdleWarning } from './IdleWarning'

export function ProtectedLayout() {
  const { session, isLoading, signOut, endSessionLocally } = useAuth()
  const location = useLocation()

  const onExpire = useCallback(
    (expiry: IdleExpiry) => {
      if (expiry === 'ended') {
        endSessionLocally()
        return
      }

      void signOut()
    },
    [signOut, endSessionLocally],
  )

  const idle = useIdleTimeout({ enabled: session !== null, onExpire })

  if (isLoading) {
    return (
      <div className="canopy-page" style={{ color: 'var(--text-3)' }}>
        Loading…
      </div>
    )
  }

  if (!session) {
    const state = isDeliberateSignOut() ? null : { from: location.pathname }

    return <Navigate to="/login" replace state={state} />
  }

  if (idle.phase === 'expired') {
    return <div className="canopy-page text-text-3">Signing out…</div>
  }

  return (
    <>
      <AppHeader />
      <Suspense
        fallback={
          <div className="canopy-page" style={{ color: 'var(--text-3)' }}>
            Loading…
          </div>
        }
      >
        <Outlet />
      </Suspense>
      <IdleWarning
        open={idle.phase === 'warning'}
        msUntilExpiry={idle.msUntilExpiry}
        onExtend={idle.extend}
        onSignOut={() => {
          void signOut()
        }}
      />
    </>
  )
}
